import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import {
	SOFTPHONE_DEFAULT_FALLBACK_KIND,
	SOFTPHONE_DEFAULT_RING_STRATEGY,
	type SoftphoneGroupRoleFallbackKindModel,
	type SoftphoneRingStrategyModel,
} from '../../constants/softphone-ring-strategies';
import { BaseEntity } from './base.entity';
import { RbacRoleEntity } from './rbac-role.entity';

/**
 * Per-RbacRole softphone routing config — overrides the tenant default
 * ring strategy when this Role (e.g. "Salesperson", "Customer Care",
 * "Sales Manager") is the destination of an inbound number or a
 * transfer. One row per Role; `fkRbacRoleId` unique.
 *
 * Mirrors `SoftphoneGroupConfigEntity` but keyed against `_rbac_role`
 * — the RBAC entity that backs the seeded org roles in
 * `SeedCoreRoles1760500000000`. Group and Role routing co-exist
 * intentionally; an admin can choose either as a hunt-group target
 * depending on whether the relevant team aligns with an org-chart
 * group or with an RBAC role membership.
 *
 * Resolution chain identical to the Group case:
 *   1. This row's `ringStrategy` — wins if present
 *   2. Tenant default — `InstanceSettingEntity.softphone.defaultRingStrategy`
 *   3. Hard-coded `simultaneous` fallback
 */
@Index('softphone_role_config_role_pk', ['fkRbacRoleId'], { unique: true })
@Entity('softphone_role_config')
export class SoftphoneRoleConfigEntity extends BaseEntity {
	@Column('int', { name: 'fk__rbac_role__id', unsigned: true })
	public fkRbacRoleId!: number;

	@Column('varchar', {
		name: 'ringStrategy',
		length: 32,
		default: () => `'${SOFTPHONE_DEFAULT_RING_STRATEGY}'`,
	})
	public ringStrategy!: SoftphoneRingStrategyModel;

	@Column('int', {
		name: 'roundRobinNextIndex',
		unsigned: true,
		default: 0,
	})
	public roundRobinNextIndex!: number;

	/**
	 * Per-attempt ring timeout for `round-robin` / `longest-idle`
	 * strategies. See `SoftphoneGroupConfigEntity.attemptTimeoutSeconds`
	 * — same semantics, mirrored here for the Role-destination case.
	 */
	@Column('int', {
		name: 'attemptTimeoutSeconds',
		unsigned: true,
		default: 5,
	})
	public attemptTimeoutSeconds!: number;

	/**
	 * No-answer fallback choice when ALL members of this Role exhaust
	 * (DD.4.E). Defaults to `'none'` — same rationale as Group / per-
	 * User configs. Forwarding kinds (`forwardToX`) are NOT valid
	 * here; only `none` / `voicemail` / `aiAgent`.
	 */
	@Column('varchar', {
		name: 'fallbackKind',
		length: 24,
		default: () => `'${SOFTPHONE_DEFAULT_FALLBACK_KIND}'`,
	})
	public fallbackKind!: SoftphoneGroupRoleFallbackKindModel;

	@Column('int', {
		name: 'fallbackAiAgentId',
		unsigned: true,
		nullable: true,
	})
	public fallbackAiAgentId!: number | null;

	@Column('varchar', {
		name: 'voicemailGreetingUrl',
		length: 512,
		nullable: true,
	})
	public voicemailGreetingUrl!: string | null;

	@ManyToOne(() => RbacRoleEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk__rbac_role__id', referencedColumnName: 'id' }])
	public fkRbacRole!: RbacRoleEntity;
}
